import { useEffect, useState } from 'react';
import { MIN_PASSWORT_LAENGE } from '../../shared/validation';

export interface PasswortZiel {
  id: string;
  bezeichnung: string;
}

/** Vorschlag im Browser erzeugen — `crypto.getRandomValues`, nicht `Math.random`. */
function vorschlag(): string {
  const zeichen = 'abcdefghijkmnpqrstuvwxyzACBDEFGHJKLMNPQRTUVWXYZ2346789';
  const werte = new Uint32Array(16);
  crypto.getRandomValues(werte);
  const roh = [...werte].map((v) => zeichen[v % zeichen.length]);
  return [roh.slice(0, 4), roh.slice(4, 8), roh.slice(8, 12), roh.slice(12, 16)]
    .map((g) => g.join(''))
    .join('-');
}

/**
 * Passwort für ein bestehendes Konto setzen.
 *
 * Das Feld ist mit einem Vorschlag vorbelegt, lässt sich aber überschreiben.
 * Beides wird gebraucht: ein erzeugtes ist besser, ein selbst gewähltes lässt
 * sich am Telefon durchgeben.
 *
 * Im Klartext, nicht als Punkte: wer das Passwort gleich weitergibt, muss
 * lesen können, was er weitergibt — und verdeckt tippt man sich genau hier
 * vertippt.
 */
export function PasswortSetzenSheet({
  ziel,
  laeuft,
  onSetzen,
  onClose,
}: {
  ziel: PasswortZiel | null;
  laeuft: boolean;
  onSetzen: (passwort: string) => void;
  onClose: () => void;
}) {
  const [wert, setWert] = useState('');
  const [hinweis, setHinweis] = useState<string | null>(null);

  useEffect(() => {
    if (!ziel) return;
    setWert(vorschlag());
    setHinweis(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !laeuft) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ziel, laeuft, onClose]);

  if (!ziel) return null;

  function absenden() {
    if (wert.length < MIN_PASSWORT_LAENGE) {
      setHinweis(`Mindestens ${MIN_PASSWORT_LAENGE} Zeichen.`);
      return;
    }
    onSetzen(wert);
  }

  return (
    <div
      className="sheet"
      role="dialog"
      aria-modal="true"
      aria-label="Passwort setzen"
      onClick={() => !laeuft && onClose()}
    >
      <div className="sheet__panel" onClick={(event) => event.stopPropagation()}>
        <div className="sheet__head">
          <h2>// passwort setzen</h2>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            disabled={laeuft}
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        <p>
          Neues Passwort für <strong>{ziel.bezeichnung}</strong>. Das bisherige gilt danach
          nicht mehr.
        </p>

        <label className="field" style={{ marginTop: '.75rem' }}>
          <span className="field__label">Passwort (min. {MIN_PASSWORT_LAENGE} Zeichen)</span>
          <input
            className="input mono"
            type="text"
            value={wert}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => {
              setWert(e.target.value);
              setHinweis(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') absenden();
            }}
          />
        </label>
        {hinweis && <p className="card__meta">{hinweis}</p>}

        <div className="form__actions" style={{ marginTop: '.75rem' }}>
          <button type="button" className="px-btn px-btn--sm" onClick={absenden} disabled={laeuft}>
            {laeuft ? 'Wird gesetzt …' : 'Passwort setzen'}
          </button>
          <button
            type="button"
            className="px-btn px-btn--ghost px-btn--sm"
            onClick={() => setWert(vorschlag())}
            disabled={laeuft}
          >
            Neu erzeugen
          </button>
          <button type="button" className="px-btn px-btn--ghost px-btn--sm" onClick={onClose} disabled={laeuft}>
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}
